import React, { useEffect, useMemo, useState } from "react";
import { message, Button as AntdButton } from "antd";
import { Box } from "@mui/material";
import StickyFlexTable from "../common/StickyFlexTable";
import TypedConfirmPopup from "../common/TypedConfirmPopup";
import ThemedTooltip from "../common/ThemedTooltip";
import ThemedChip from "../common/ThemedChip";
import { cancelCallsOrBatch, getCallsList } from "../../utils/apiClient";
import { formatDateTime, isCancelable } from "../../utils/date-utils";

// For call-level cancellation, enable when cancelBefore > now (no extra safety window)
const CANCEL_DISABLE_THRESHOLD_MS = 0;

export default function CallStatus({
  rows = [],
  loading = false,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  refresh,
}) {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const isCallCancelable = (row) => {
    const result = isCancelable(row, now, CANCEL_DISABLE_THRESHOLD_MS);
    return result;
  };

  // Helper function to get variant for call status
  const getCallStatusVariant = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'cancelled':
      case 'canceled':
        return 'error';
      case 'in-progress':
      case 'running':
      case 'active':
        return 'info';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper function to get variant for call outcome
  const getCallOutcomeVariant = (outcome) => {
    const outcomeLower = outcome?.toLowerCase();
    switch (outcomeLower) {
      case 'successful':
      case 'success':
        return 'success';
      case 'failure':
      case 'failed':
      case 'error':
        return 'error';
      case 'partial':
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const enhancedRows = useMemo(() => {
    return (rows || []).map((row, idx) => ({
      ...row,
      id: row.callRequestId || idx,
      patientName: [
        row.memberFirstName,
        row.memberMiddleName,
        row.memberLastName,
      ]
        .filter(Boolean)
        .join(" "),
      cancelBeforeLocal: row.cancelBefore
        ? formatDateTime(row.cancelBefore)
        : "-",
    }));
  }, [rows]);

  // formatDateTime now imported from utils

  const columns = [
    { 
      headerName: "Call Status", 
      renderName: "callStatus",
      minWidth: 120,
      renderCell: (row) => {
        if (!row.callStatus) return '';
        return (
          <ThemedChip variant={getCallStatusVariant(row.callStatus)}>
            {row.callStatus}
          </ThemedChip>
        );
      }
    },
    { 
      headerName: "Call Outcome", 
      renderName: "callOutcome",
      minWidth: 120,
      renderCell: (row) => {
        if (!row.callOutcome) return '';
        return (
          <ThemedChip variant={getCallOutcomeVariant(row.callOutcome)}>
            {row.callOutcome}
          </ThemedChip>
        );
      }
    },
    { headerName: "Call Start Time", renderName: "callStartTime" },
    { headerName: "Call End Time", renderName: "callEndTime" },
    { headerName: "Patient Name", renderName: "patientName" },
    { headerName: "Primary Member ID", renderName: "primaryMemberId" },
    // { headerName: "Cancel Before", renderName: "cancelBeforeLocal" },
    {
      renderName: "singleCancel",
      headerName: "Action",
      minWidth: 60,
      align: "center",
      fixed: true,
      renderCell: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {isCallCancelable(row) && (
            <ThemedTooltip
              title="Cancel this scheduled call. This action cannot be undone."
              placement="top"
            >
              <Box
                sx={{ 
                  display: "flex", 
                  justifyContent: "center",
                  cursor: "pointer"
                }}
                onClick={() => {
                  setSelectedKeys([row.id]);
                  setConfirmVisible(true);
                }}
              >
                <img
                  src={`/icons/cancel-icon.png`}
                  alt="Cancel Call"
                  style={{
                    width: 18,
                    height: 18,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                />
              </Box>
            </ThemedTooltip>
          )}
        </Box>
      ),
    },
  ];

  const toolbar = (
    <Box sx={{ display: "flex", gap: 1 }}>
      {selectedKeys.length > 0 && (
        <AntdButton
          type="primary"
          danger
          onClick={() => setConfirmVisible(true)}
        >
          Cancel {selectedKeys.length}{" "}
          {selectedKeys.length === 1 ? "Call" : "Calls"}
        </AntdButton>
      )}
    </Box>
  );

  const handleConfirmCancel = async () => {
    try {
      message.loading("Cancelling calls...", 0);
      const res = await cancelCallsOrBatch({
        cancelType: "call",
        ids: selectedKeys,
      });
      message.destroy();
      if (res?.statusCode === "SUCCESS") {
        message.success("Calls cancelled successfully");
        setConfirmVisible(false);
        setSelectedKeys([]);
        
        // Refresh the calls data
        if (refresh) {
          await refresh();
        } else {
          // Fallback: refresh using stored batch ID if refresh prop is not available
          const batchId = sessionStorage.getItem('batchId');
          if (batchId) {
            await getCallsList({ batchId });
            // Note: This fallback won't update the parent component's state
            // The refresh prop from parent is preferred
            console.log('Fallback refresh completed, but parent state may not be updated');
          }
        }
      }
    } catch (error) {
      message.destroy();
      const userMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to cancel calls";
      message.error(userMsg);
    }
  };

  return (
    <>
      <StickyFlexTable
        columns={columns}
        rows={enhancedRows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        containerHeight="70vh"
        toolbar={toolbar}
        selectionEnabled
        selectionKey="id"
        selectedKeys={selectedKeys}
        onSelectedKeysChange={setSelectedKeys}
        showSelectAll
        isRowSelectable={(row) => isCallCancelable(row)}
      />

      <TypedConfirmPopup
        visible={confirmVisible}
        title="Cancel Calls"
        message={
          selectedKeys.length > 0 ? (
            <>
              You are about to cancel {selectedKeys.length}{" "}
              {selectedKeys.length === 1 ? "call" : "calls"}.
              <br />
              <br />
              This action cannot be undone.
            </>
          ) : (
            "This action cannot be undone."
          )
        }
        confirmWord="CONFIRM"
        confirmButtonLabel="CONFIRM"
        secondaryButtonLabel="Close"
        onConfirm={handleConfirmCancel}
        onClose={() => setConfirmVisible(false)}
      />
    </>
  );
}
